import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { LenderApproval, CompareApprovalsTab, ApprovalBadge, GqlCreditSubmissionApproval } from '../types';
import { COMPARISON_ROWS } from '../constants';
import { computeBadges, computeConditionsBadges, isApproved, mapCreditSubmissionToApproval } from '../lib';
import { GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID } from '../queries';
import { EXAMPLE_APPROVAL } from '../lib/mockData';

export const useCompareApprovals = (dynamicsVoId?: string) => {
  const [activeTab, setActiveTab] = useState<CompareApprovalsTab>('prescreen');

  const { data, loading, error, refetch } = useQuery(GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID, {
    variables: { voId: dynamicsVoId },
    skip: !dynamicsVoId,
    fetchPolicy: 'network-only',
  });

  const rawCreditSubmissions = useMemo<GqlCreditSubmissionApproval[]>(
    () =>
      (data?.creditSubmissionsByDynamicsVOId ?? []).filter(
        (cs: any): cs is GqlCreditSubmissionApproval => cs != null && isApproved(cs)
      ),
    [data]
  );

  const approvals = useMemo<LenderApproval[]>(() => {
    const mapped = rawCreditSubmissions.map(mapCreditSubmissionToApproval);

    // Show the example card whenever there are 0 or 1 real approvals.
    return mapped.length <= 1 ? [...mapped, EXAMPLE_APPROVAL] : mapped;
  }, [rawCreditSubmissions]);

  const badgesByRow = useMemo(() => {
    // Badges are only meaningful for real approvals – exclude the example card.
    const realApprovals = approvals.filter(a => !a.isExample);
    const badges: Record<string, Record<string, ApprovalBadge>> = {};

    COMPARISON_ROWS.forEach(row => {
      if (row.key === 'conditions') {
        badges[row.key] = computeConditionsBadges(realApprovals);
      } else {
        badges[row.key] = computeBadges(realApprovals, row);
      }
    });

    return badges;
  }, [approvals]);

  return {
    activeTab,
    setActiveTab,
    approvals,
    rawCreditSubmissions,
    badgesByRow,
    loading,
    error,
    refetch,
  };
};
